import { useCallback, useEffect, useRef } from "react";

/** The slice of the Screen Wake Lock API this hook uses. */
interface ScreenWakeLock {
  request(type: "screen"): Promise<{ released: boolean; release(): Promise<void> }>;
}

function wakeLockApi(): ScreenWakeLock | null {
  if (typeof navigator === "undefined") return null;
  const api = (navigator as Navigator & { wakeLock?: ScreenWakeLock }).wakeLock;
  return api && typeof api.request === "function" ? api : null;
}

/**
 * KEEP THE WALL SCREEN AWAKE (T16).
 *
 * A board that dims after two minutes is a board nobody reads. `acquire()` is
 * called from the start overlay's tap; from then on the lock is re-taken each
 * time the page becomes visible again (the browser drops it whenever the tab
 * is hidden), and released when the board unmounts. Where the API does not
 * exist, or the request is refused (low battery, a policy), it does nothing —
 * the board works exactly as it did, just with the device's own sleep timer.
 */
export function useScreenWakeLock(): { acquire: () => void } {
  const wanted = useRef(false);
  const alive = useRef(true);
  const sentinel = useRef<{ released: boolean; release(): Promise<void> } | null>(null);

  const request = useCallback(async () => {
    const api = wakeLockApi();
    if (!api) return;
    try {
      const lock = await api.request("screen");
      if (!alive.current) {
        // The board went away while the request was in flight.
        void lock.release().catch(() => undefined);
        return;
      }
      sentinel.current = lock;
    } catch {
      // Refused or unsupported in this state: the device's own timer stands.
    }
  }, []);

  const acquire = useCallback(() => {
    wanted.current = true;
    if (sentinel.current && !sentinel.current.released) return;
    void request();
  }, [request]);

  useEffect(() => {
    alive.current = true;
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !wanted.current) return;
      if (sentinel.current && !sentinel.current.released) return;
      void request();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      const lock = sentinel.current;
      sentinel.current = null;
      if (lock && !lock.released) void lock.release().catch(() => undefined);
    };
  }, [request]);

  return { acquire };
}
